// Emoji picker for the message composer.
//
// The web client had no way to insert an emoji at all — the composer toolbar
// was photo / attach / textarea / send, so on desktop the only route was the
// OS emoji panel. This is the in-app picker: a recently-used row persisted in
// localStorage, category tabs, and search over the same alias table iOS uses.

import React from 'react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import { EMOJI_CATEGORIES, recentEmojis, recordEmojiUse, searchEmoji } from '../lib/emoji.js';

const messages = defineMessages({
  search_emoji: {
    id: 'search_emoji',
    defaultMessage: 'Search emoji',
    description: 'Placeholder in the emoji picker search box'
  },
  recently_used: {
    id: 'emoji_recently_used',
    defaultMessage: 'Recently used',
    description: 'Title of the recently-used section of the emoji picker'
  },
  no_emoji_found: {
    id: 'no_emoji_found',
    defaultMessage: 'No emoji found',
    description: 'Shown when an emoji search returns nothing'
  },
});

class EmojiPicker extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      query: '',
      category: EMOJI_CATEGORIES[0].id,
      recents: recentEmojis(),
    };
    this.handleQuery = this.handleQuery.bind(this);
    this.handlePick = this.handlePick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.selfRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleKeyDown);
    if (this.searchRef) {
      this.searchRef.focus();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleOutsideClick(e) {
    if (this.selfRef.current && !this.selfRef.current.contains(e.target)) {
      this.props.onClose();
    }
  }

  handleKeyDown(e) {
    if (e.key == 'Escape') {
      this.props.onClose();
    }
  }

  handleQuery(e) {
    this.setState({query: e.target.value});
  }

  handlePick(emoji, e) {
    e.preventDefault();
    // Persist first so the recents row is right even if the parent closes us.
    const recents = recordEmojiUse(emoji);
    this.setState({recents: recents});
    this.props.onEmojiSelected(emoji);
  }

  render() {
    const {formatMessage} = this.props.intl;
    const searching = !!this.state.query.trim();
    const results = searching ? searchEmoji(this.state.query) : null;

    const grid = (title, emojis, key) => (
      <div className="emoji-section" key={key}>
        <div className="emoji-section-title">{title}</div>
        <div className="emoji-grid">
          {emojis.map((e, i) =>
            <a href="#" key={e + i} title={e} aria-label={e}
              onClick={this.handlePick.bind(this, e)}>{e}</a>)}
        </div>
      </div>
    );

    let body;
    if (searching) {
      body = results.length ?
        grid(null, results, 'results') :
        <div className="emoji-empty">{formatMessage(messages.no_emoji_found)}</div>;
    } else {
      const cat = EMOJI_CATEGORIES.find(c => c.id == this.state.category) || EMOJI_CATEGORIES[0];
      body = (
        <>
          {this.state.recents.length ?
            grid(formatMessage(messages.recently_used), this.state.recents, 'recents') : null}
          {grid(cat.title, cat.emojis, cat.id)}
        </>
      );
    }

    return (
      <div className="emoji-picker" ref={this.selfRef}>
        <div className="emoji-picker-search">
          <input type="text" value={this.state.query} onChange={this.handleQuery}
            placeholder={formatMessage(messages.search_emoji)}
            ref={ref => {this.searchRef = ref;}} />
        </div>
        <div className="emoji-picker-body">{body}</div>
        {searching ? null :
          <div className="emoji-picker-tabs">
            {EMOJI_CATEGORIES.map(c =>
              <a href="#" key={c.id} title={c.title} aria-label={c.title}
                className={c.id == this.state.category ? 'active' : null}
                onClick={e => {e.preventDefault(); this.setState({category: c.id});}}>{c.icon}</a>)}
          </div>}
      </div>
    );
  }
}

export default injectIntl(EmojiPicker, {forwardRef: true});
